import * as RadixTooltip from '@radix-ui/react-tooltip'
import uniqueId from 'lodash/uniqueId'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { ZIndex } from '@opallabs/gem'
import { InverseThemeProvider, ThemeProvider, useTheme } from '@opallabs/hooks'
import * as tooltipStyles from '@opallabs/lapidary/src/components/Tooltip/styles'

/** Long enough not to fire while a cursor crosses something on its way past. */
export const TOOLTIP_DELAY = 250

/** Where the tooltip points, read from the chip when the pointer arrives. */
type Anchor = Pick<DOMRect, 'height' | 'left' | 'top' | 'width'>

type LiveTooltip = {
  anchor: Anchor
  content: ReactNode
  /** The chip itself, which is asked whether it is still on the page. */
  element: HTMLElement
  itemId: string
}

/**
 * The tooltip itself, pointed at the chip a person is on.
 *
 * It stands on a marker of the chip's size rather than on the chip, which is
 * what lets one tooltip serve a whole day. The marker takes no clicks, so
 * everything a chip does — opening, dragging, its own menu — is untouched by
 * the tooltip standing over it. Radix still does the placing, the flipping and
 * the screenreader copy.
 */
const ItemTooltipContent: React.FC<{
  anchor: Anchor
  children: ReactNode
  id: string
}> = ({ anchor, children, id }) => {
  const theme = useTheme()

  return createPortal(
    <RadixTooltip.Provider>
      <RadixTooltip.Root open={true}>
        <RadixTooltip.Trigger asChild>
          <span
            aria-hidden={true}
            style={{
              blockSize: anchor.height,
              inlineSize: anchor.width,
              insetBlockStart: anchor.top,
              insetInlineStart: anchor.left,
              pointerEvents: 'none',
              position: 'fixed',
            }}
          />
        </RadixTooltip.Trigger>
        <ThemeProvider theme={theme}>
          <RadixTooltip.Content
            collisionPadding={8}
            side="top"
            sideOffset={4}
            // Nothing here is for the pointer: the tooltip follows the chip
            // under it, and a tooltip that took the pointer would hide the
            // chip that opened it.
            style={{ pointerEvents: 'none', zIndex: ZIndex.Tooltip }}
          >
            <div css={tooltipStyles.content(theme)} id={id}>
              <InverseThemeProvider>{children}</InverseThemeProvider>
            </div>
          </RadixTooltip.Content>
        </ThemeProvider>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>,
    document.body
  )
}

/**
 * One tooltip for every chip of a day.
 *
 * A tooltip of its own on every chip is what the month used to draw, and a
 * Radix root costs far more than the chip it sits on: a busy day of a thousand
 * chips paid for a thousand of them so that one could ever be read. Here the
 * chips carry nothing but their handlers, and the day mounts a tooltip for the
 * one chip a person is on.
 *
 * `content` is a function so that only the chip being read builds its tooltip.
 */
export const useItemTooltip = () => {
  const id = useMemo(() => uniqueId('calendar-item-tooltip-'), [])
  const [live, setLive] = useState<LiveTooltip>()
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const isPointerDown = useRef(false)

  const stopWaiting = useCallback(() => {
    if (timer.current !== undefined) clearTimeout(timer.current)
    timer.current = undefined
  }, [])

  useEffect(() => stopWaiting, [stopWaiting])

  const hide = useCallback(() => {
    stopWaiting()
    setLive(undefined)
  }, [stopWaiting])

  const show = useCallback(
    (
      itemId: string,
      content: () => ReactNode,
      element: HTMLElement,
      delay: number
    ) => {
      stopWaiting()
      const { height, left, top, width } = element.getBoundingClientRect()
      const next = { anchor: { height, left, top, width }, element, itemId }

      // The keyboard has no way to rest on something, so a chip tabbed to says
      // what it is at once.
      if (delay === 0) setLive({ ...next, content: content() })
      else
        timer.current = setTimeout(
          () => setLive({ ...next, content: content() }),
          delay
        )
    },
    [stopWaiting]
  )

  // The tooltip stands where the chip stood when it opened, so anything that
  // moves the grid under it leaves it pointing at nothing. The grid scrolls in
  // a container of its own, which is why the listener captures.
  const isLive = live !== undefined
  useEffect(() => {
    if (!isLive) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('scroll', hide, true)
    window.addEventListener('resize', hide)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('scroll', hide, true)
      window.removeEventListener('resize', hide)
    }
  }, [hide, isLive])

  // A chip can go away with the pointer still on it — a filter, a month drawn
  // again — and nothing says so, because the chip that would have said it is
  // the one that left.
  useEffect(() => {
    if (live !== undefined && !live.element.isConnected) hide()
  })

  /** What a chip carries so that resting on it, or tabbing to it, says more. */
  const triggerProps = (itemId: string, content: () => ReactNode) => ({
    'aria-describedby': live?.itemId === itemId ? id : undefined,
    onBlur: hide,
    // A chip clicked with the mouse is focused by the click, and a tooltip
    // that answered that would stand over what the click just opened.
    onFocus: (event: React.FocusEvent<HTMLElement>) => {
      const wasClicked = isPointerDown.current
      isPointerDown.current = false
      if (!wasClicked) show(itemId, content, event.currentTarget, 0)
    },
    onClick: hide,
    onPointerDown: () => {
      isPointerDown.current = true
      document.addEventListener(
        'pointerup',
        () => {
          isPointerDown.current = false
        },
        { capture: true, once: true }
      )
    },
    onPointerEnter: (event: React.PointerEvent<HTMLElement>) =>
      show(itemId, content, event.currentTarget, TOOLTIP_DELAY),
    onPointerLeave: hide,
  })

  return {
    tooltip:
      live === undefined ? null : (
        <ItemTooltipContent anchor={live.anchor} id={id}>
          {live.content}
        </ItemTooltipContent>
      ),
    triggerProps,
  }
}
